import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, RotateCw, AlertTriangle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

export default function SoloBilling() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [b] = await Promise.all([api.get("/billing/me")]);
      setBilling(b.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { loadAll(); }, []);

  const openCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/billing/cancel-preview");
      setPreview(data);
      setDlg(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const confirmCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel-with-refund");
      toast.success(
        data.refund_usd > 0
          ? `Cancelled — refund request for $${data.refund_usd.toFixed(2)} filed`
          : "Subscription cancelled"
      );
      setDlg(false);
      await refresh?.();
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const renew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/renew");
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  const planLabel = {
    annual_pass: "Annual Pass",
    monthly_flex: "Monthly Flex",
  }[billing?.plan] || billing?.plan || "—";

  const exp = billing?.expires_at ? new Date(billing.expires_at) : null;
  const daysLeft = exp ? Math.max(0, Math.ceil((exp - new Date()) / 86400000)) : 0;

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: "var(--ink-1)" }} data-testid="solo-billing-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Button variant="ghost" onClick={() => nav("/portal")} className="text-white/70 hover:text-white" data-testid="billing-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Portal
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 pt-8">
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Subscription</div>
        <h1 className="font-display font-black text-3xl tracking-tighter text-white mt-1">My Billing</h1>

        {!billing && <div className="mt-8 text-sm text-white/60">Loading…</div>}

        {billing && (
          <div className="mt-6 rounded-2xl ink-card p-6" data-testid="billing-summary">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: billing.active ? "#16A34A" : "#F59E0B" }}>
                  {billing.active ? "Active" : "Inactive"}
                </div>
                <div className="font-display font-black text-2xl text-white mt-1">{planLabel}</div>
              </div>
              {billing.active && (
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-white/40">Days remaining</div>
                  <div className="font-display font-black text-3xl font-mono-num text-white">{daysLeft}</div>
                </div>
              )}
            </div>

            {billing.active && exp && (
              <div className="mt-4 flex items-center gap-2 text-sm text-white/70">
                <CalendarClock className="h-4 w-4" /> Renews on <span className="font-mono-num font-bold text-white">{exp.toLocaleDateString()}</span>
              </div>
            )}
            {billing.parent_email && (
              <div className="mt-1 text-xs text-white/40">Parent on digest: {billing.parent_email}</div>
            )}

            {billing.active && (
              <div className="mt-5 grid grid-cols-2 gap-2">
                <Button onClick={renew} disabled={busy} className="text-white font-bold rounded-full red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="renew-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCw className="h-4 w-4 mr-2" /> Renew now</>}
                </Button>
                <Button onClick={openCancel} disabled={busy} variant="outline" className="border-white/15 text-white bg-white/5 hover:bg-white/10 rounded-full" data-testid="cancel-btn">
                  Cancel
                </Button>
              </div>
            )}
            {!billing.active && billing.archived_at && (
              <div className="mt-5 rounded-lg p-3 text-sm text-white/80" style={{ backgroundColor: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }}>
                <AlertTriangle className="h-4 w-4 inline mr-2 text-amber-300" />
                Cancelled — data archives on <span className="font-mono-num font-bold">{new Date(billing.purge_after).toLocaleDateString()}</span>. Re-subscribe before then to restore everything.
              </div>
            )}
            {!billing.active && !billing.archived_at && (
              <div className="mt-5">
                <Link to="/pricing"><Button className="w-full text-white font-bold rounded-full red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}>Re-subscribe</Button></Link>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white sm:max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tighter">Cancel your subscription?</DialogTitle>
            <DialogDescription className="text-white/60">
              All sales are final per our <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline hover:text-white">Refund Policy</a>. Your access continues until the end of the current period. After cancellation, Monthly-Flex data archives for 30 days then is permanently deleted.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-3 my-2">
              <div className="rounded-lg p-4 grid grid-cols-3 gap-2 text-center" style={{ backgroundColor: "var(--ink-1)" }}>
                <div>
                  <div className="text-[9px] tracking-[0.2em] font-bold uppercase text-white/40">Paid</div>
                  <div className="font-display font-black text-xl font-mono-num text-white">${preview.amount_paid_usd?.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.2em] font-bold uppercase text-white/40">Days left</div>
                  <div className="font-display font-black text-xl font-mono-num text-white">{preview.days_remaining}</div>
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.2em] font-bold uppercase text-white/40">Refund</div>
                  <div className="font-display font-black text-xl font-mono-num text-white/30" data-testid="refund-amount">$0.00</div>
                </div>
              </div>
              <p className="text-xs text-white/60 leading-relaxed">
                Per the Refund Policy, no pro-rated refunds are issued. You'll keep platform access until your period ends.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDlg(false)} disabled={busy} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="cancel-dialog-back">
              Keep subscription
            </Button>
            <Button onClick={confirmCancel} disabled={busy} className="text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="cancel-dialog-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirm cancel</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
